// "Install app" section for the reader Tools menu. A read-only projection of the
// resolved InstallMode (see lib/pwaInstall.ts):
//   - prompt:    a real Install button that fires the captured native prompt
//   - guide:     a short, device-accurate "Add to Home Screen" walkthrough
//   - installed: nothing (returns null) - the app is already standalone
// The section rebuilds with the Tools menu on every reader mount, so a prompt
// captured after first paint is picked up on the next open.

import { el, button } from "../lib/dom.ts";
import type { InstallMode } from "../lib/pwaInstall.ts";

export interface InstallSectionDeps {
  mode: InstallMode;
  // Fires the captured native install prompt; resolves with the user's choice.
  onInstall: () => Promise<"accepted" | "dismissed" | "unavailable">;
}

export function buildInstallSection(deps: InstallSectionDeps): HTMLElement | null {
  if (deps.mode.kind === "installed") return null;

  const section = el("section", "rc-install");
  section.setAttribute("aria-label", "Install TorahTrail");

  const head = el("div", "rc-row");
  head.append(el("span", "rc-label", "Install app"));

  if (deps.mode.kind === "prompt") {
    const installBtn = button("rc-btn rc-install-btn", "Install", () => {
      void deps.onInstall().then((outcome) => {
        if (outcome === "accepted") section.remove();
      });
    });
    installBtn.setAttribute("aria-label", "Install TorahTrail on this device");
    head.append(installBtn);
    section.append(head);
    return section;
  }

  // guide mode: title in the head row, numbered steps below.
  const guide = deps.mode.guide;
  head.append(el("span", "rc-install-plat", guide.title));
  section.append(head);
  const ol = el("ol", "rc-install-steps");
  for (const step of guide.steps) ol.append(el("li", "rc-install-step", step));
  section.append(ol);
  return section;
}
